import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import { Banner } from '@shared/ui/feedback/Banner';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { useAuthStore } from '@shared/stores/authStore';
import { toApiError } from '@shared/api/errors';
import { Onboarding } from '@features/onboarding/Onboarding';
import {
  completeAccessOnboarding,
  fetchAccessCredential,
  fetchAccessOnboardingContext,
  startAccessOnboarding,
  type AccessCredentialReveal,
} from './api';

type CredentialRow = { method: string; qr_payload?: string | null };

function methodLabel(method: string): string {
  const map: Record<string, string> = {
    pin: 'PIN',
    otp: 'OTP',
    qr: 'QR code',
    driver_license: "Driver's licence",
    fingerprint: 'Fingerprint',
    facial: 'Facial recognition',
    liveness: 'Liveness check',
  };
  return map[method.toLowerCase()] ?? method;
}

function buildCredentialRows(credential: AccessCredentialReveal): CredentialRow[] {
  if (credential.issued_credentials?.length) {
    return credential.issued_credentials;
  }
  const rows: CredentialRow[] = [];
  for (const method of credential.methods ?? []) {
    const lower = method.toLowerCase();
    rows.push({
      method: lower,
      qr_payload: lower === 'qr' ? credential.qr_payload ?? null : null,
    });
  }
  return rows.length ? rows : [{ method: 'pin', qr_payload: null }];
}

/**
 * Visitor onboarding before access credentials — `/access/onboarding/:accessInviteId/:accessToken`.
 * Mirrors member-portal AccessOnboardingComponent: embed flow inline, then issue + reveal PIN.
 */
export function PublicAccessOnboarding(): JSX.Element {
  const { accessInviteId = '', accessToken = '' } = useParams<{
    accessInviteId: string;
    accessToken: string;
  }>();
  const tenantId = useAuthStore((s) => s.tenantId);

  const [loadingContext, setLoadingContext] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [flowInstanceId, setFlowInstanceId] = useState<string | null>(null);
  const [finishing, setFinishing] = useState(false);
  const [credential, setCredential] = useState<AccessCredentialReveal | null>(null);
  const [credentialRows, setCredentialRows] = useState<CredentialRow[]>([]);
  const [plainPin, setPlainPin] = useState<string | null>(null);
  const [qrUrls, setQrUrls] = useState<Record<string, string>>({});
  const finishStarted = useRef(false);

  const loadCredentialReveal = useCallback(async () => {
    const cred = await fetchAccessCredential(accessInviteId, accessToken);
    setCredential(cred);
    const rows = buildCredentialRows(cred);
    setCredentialRows(rows);

    const nextQr: Record<string, string> = {};
    for (const row of rows) {
      if (row.method === 'qr' && row.qr_payload) {
        try {
          nextQr[row.method] = await QRCode.toDataURL(row.qr_payload, { width: 220, margin: 2 });
        } catch {
          /* skip row */
        }
      }
    }
    setQrUrls(nextQr);
  }, [accessInviteId, accessToken]);

  const finishAfterFlow = useCallback(async () => {
    if (finishStarted.current) return;
    finishStarted.current = true;
    setFinishing(true);
    setErrorMessage('');
    try {
      const complete = await completeAccessOnboarding(accessInviteId, accessToken);
      if (complete.pin) {
        setPlainPin(String(complete.pin));
      }
      await loadCredentialReveal();
      setFlowInstanceId(null);
    } catch (err) {
      setErrorMessage(toApiError(err).message || 'Could not finalize access');
    } finally {
      setFinishing(false);
    }
  }, [accessInviteId, accessToken, loadCredentialReveal]);

  useEffect(() => {
    if (!accessInviteId || !accessToken || !tenantId) {
      setLoadingContext(false);
      if (!tenantId && accessInviteId && accessToken) {
        setErrorMessage('Invalid link or missing tenant context. Reload the page.');
      }
      return;
    }

    let cancelled = false;

    void (async () => {
      setLoadingContext(true);
      setErrorMessage('');
      setFlowInstanceId(null);
      setCredential(null);
      setPlainPin(null);
      setCredentialRows([]);
      setQrUrls({});
      finishStarted.current = false;

      try {
        const ctx = await fetchAccessOnboardingContext(accessInviteId, accessToken);
        const needsOnboarding = !!(ctx.require_onboarding_before_accept && ctx.onboarding_type_id);

        if (!needsOnboarding) {
          const complete = await completeAccessOnboarding(accessInviteId, accessToken);
          if (cancelled) return;
          if (complete.pin) {
            setPlainPin(String(complete.pin));
          }
          await loadCredentialReveal();
          return;
        }

        if (ctx.flow_instance_id) {
          if (cancelled) return;
          setFlowInstanceId(ctx.flow_instance_id);
          return;
        }

        const start = await startAccessOnboarding(accessInviteId, accessToken);
        if (cancelled) return;
        if (!start.flow_instance_id) {
          setErrorMessage('Could not start onboarding');
          return;
        }
        setFlowInstanceId(start.flow_instance_id);
      } catch (err) {
        if (!cancelled) {
          setErrorMessage(toApiError(err).message || 'Request failed');
        }
      } finally {
        if (!cancelled) {
          setLoadingContext(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accessInviteId, accessToken, tenantId, loadCredentialReveal]);

  if (!accessInviteId || !accessToken) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-6">
        <Banner variant="danger">Invalid link.</Banner>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="mx-auto max-w-lg px-6 py-10">
        <h1 className="font-display text-display-md text-ink mb-2">Access invite</h1>
        <Banner variant="danger">{errorMessage}</Banner>
      </div>
    );
  }

  if (loadingContext || finishing) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-6">
        <Skeleton className="h-8 w-48" />
      </div>
    );
  }

  if (credential) {
    return (
      <div className="mx-auto max-w-lg px-6 py-10">
        <h1 className="font-display text-display-md text-ink mb-2">Your access details</h1>
        <p className="mb-6 text-body text-ink-secondary">Reference: {credential.reference}</p>
        <div className="space-y-4">
          {credentialRows.map((row) => (
            <div key={row.method} className="rounded-xl bg-card p-4 hairline">
              <p className="mb-2 text-body font-medium text-ink">{methodLabel(row.method)}</p>

              {(row.method === 'pin' || row.method === 'otp') && (
                <>
                  {plainPin ? (
                    <p className="font-mono text-2xl tracking-widest">{plainPin}</p>
                  ) : credential.pin_available ? (
                    <p className="text-body text-ink-secondary">
                      PIN was issued earlier. Check your notification message.
                    </p>
                  ) : (
                    <p className="text-body text-ink-secondary">
                      No PIN shown here. Use the message sent to you or ask your host.
                    </p>
                  )}
                </>
              )}

              {row.method === 'qr' && row.qr_payload && qrUrls.qr ? (
                <img src={qrUrls.qr} alt="Access QR code" className="mx-auto max-w-[220px]" />
              ) : null}

              {row.method === 'driver_license' && (
                <p className="text-body text-ink-secondary">
                  Have your driver licence ready for verification at the gate.
                </p>
              )}

              {(row.method === 'fingerprint' ||
                row.method === 'facial' ||
                row.method === 'liveness') && (
                <p className="text-body text-ink-secondary">
                  Biometric step may be completed at the gate reader.
                </p>
              )}
            </div>
          ))}
        </div>

        {credential.biometric_hint ? (
          <p className="mt-4 text-body text-ink-secondary">{credential.biometric_hint}</p>
        ) : null}
      </div>
    );
  }

  if (flowInstanceId) {
    return (
      <div className="mx-auto max-w-lg px-6 py-6">
        <Onboarding embedInstanceId={flowInstanceId} onEmbedComplete={() => void finishAfterFlow()} />
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh items-center justify-center p-6">
      <Skeleton className="h-8 w-48" />
    </div>
  );
}
